// Hierarchical scope-access + confidential-wall API client. The + Add create door grants direct
// access at create-time; Governance lists/adds/removes direct access and manages wall state. Access
// assignments inherit downward, while ethical-wall membership remains an additional backend gate.
// Cookie sessions are required by every /scope endpoint.

const API_BASE = "/api/workspace/scope";
const FETCH_OPTS: RequestInit = { credentials: "include" };

/** A direct member assignment at a workspace/client/engagement/project scope. */
export interface ScopeMemberRow {
  user_id: string;
  email: string;
  display_name: string | null;
  platform_role: string;
  role: string;
}

/** An approved Velocity Workspace user eligible for a scope assignment. */
export interface ApprovedScopeUser {
  user_id: string;
  email: string;
  display_name: string | null;
  platform_role: string;
}

async function readErrorDetail(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { detail?: unknown };
    return typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail ?? "");
  } catch {
    return "";
  }
}

/** Direct assignments at this node. Inherited assignments are resolved by the backend. */
export async function listScopeMembers(nodeId: string): Promise<ScopeMemberRow[]> {
  const res = await fetch(`${API_BASE}/nodes/${encodeURIComponent(nodeId)}/members`, FETCH_OPTS);
  if (!res.ok) throw new Error(`members failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
  return (await res.json()) as ScopeMemberRow[];
}

/** Search active users already approved for Velocity Workspace. */
export async function searchApprovedScopeUsers(
  nodeId: string,
  query: string,
): Promise<ApprovedScopeUser[]> {
  const params = new URLSearchParams({ q: query });
  const res = await fetch(
    `${API_BASE}/nodes/${encodeURIComponent(nodeId)}/eligible-users?${params.toString()}`,
    FETCH_OPTS,
  );
  if (!res.ok) throw new Error(`user search failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
  return (await res.json()) as ApprovedScopeUser[];
}

/** Grant access by approved-user email. Returns the updated direct roster. */
export async function addScopeMember(nodeId: string, email: string): Promise<ScopeMemberRow[]> {
  const res = await fetch(`${API_BASE}/nodes/${encodeURIComponent(nodeId)}/members`, {
    ...FETCH_OPTS,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(`add member failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
  return (await res.json()) as ScopeMemberRow[];
}

/** Remove a direct scope assignment by app-user id. */
export async function removeScopeMember(nodeId: string, userId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/nodes/${encodeURIComponent(nodeId)}/members/${encodeURIComponent(userId)}`,
    { ...FETCH_OPTS, method: "DELETE" },
  );
  if (!res.ok) throw new Error(`remove member failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
}

/** Seal (confidential=true) or unseal (false) a node's wall — ADMIN/member gated + audited on the BE. */
export async function setScopeWall(nodeId: string, confidential: boolean): Promise<void> {
  const res = await fetch(`${API_BASE}/nodes/${encodeURIComponent(nodeId)}/wall`, {
    ...FETCH_OPTS,
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ confidential }),
  });
  if (!res.ok) throw new Error(`wall change failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
}
